import { useEffect, useState } from "react";
import { shopifyImg } from "@/lib/shopifyImage";

type Section = {
  alt: string;
  images: string[];
};

const sections: Section[] = [
  {
    alt: "Pickleball at golden hour",
    images: [
      "https://cdn.shopify.com/s/files/1/0808/1332/6620/files/IMG-8698.png?v=1721837076",
      "https://cdn.shopify.com/s/files/1/0808/1332/6620/files/72E4CC42-6C48-4166-8830-0F514EDAE03F.jpg?v=1722660089",
      "https://cdn.shopify.com/s/files/1/0808/1332/6620/files/0B99ADD6-0420-4705-AADE-6DB162E89D02.png?v=1765165220",
    ],
  },
  {
    alt: "Mid-swing on the fairway",
    images: [
      "https://cdn.shopify.com/s/files/1/0808/1332/6620/files/IMG-6172.png?v=1781401862",
      "https://cdn.shopify.com/s/files/1/0808/1332/6620/files/Shopify_Slideshow_Images_-_2.png?v=1765204700",
      "https://cdn.shopify.com/s/files/1/0808/1332/6620/files/IMG-8691.png?v=1721837281",
    ],
  },
  {
    alt: "Offshore on the bow",
    images: [
      "https://cdn.shopify.com/s/files/1/0808/1332/6620/files/IMG-8704.png?v=1721836813",
      "https://cdn.shopify.com/s/files/1/0808/1332/6620/files/IMG_9396.jpg?v=1706758562",
      "https://cdn.shopify.com/s/files/1/0808/1332/6620/files/Your_paragraph_text_8586bac6-1a53-40e4-854d-421df536a11f.png?v=1705362977",
    ],
  },
  {
    alt: "Tailgate at sunset",
    images: [
      "https://cdn.shopify.com/s/files/1/0808/1332/6620/files/Your_paragraph_text_51fe2feb-1f5f-44d4-b318-c101426b8565.png?v=1703388095",
      "https://cdn.shopify.com/s/files/1/0808/1332/6620/files/IMG_9359.jpg?v=1703359618",
      "https://cdn.shopify.com/s/files/1/0808/1332/6620/files/A1A4F653-F544-4BEA-82A4-2B3EEA8C419F.jpg?v=1782921824",
    ],
  },
  {
    alt: "Walking the wheat at sunrise",
    images: [
      "https://cdn.shopify.com/s/files/1/0808/1332/6620/files/F51048CF-B6A1-4FDC-9814-196DE253600C.jpg?v=1722659970",
      "https://cdn.shopify.com/s/files/1/0808/1332/6620/files/IMG_6277.jpg?v=1700719423",
      "https://cdn.shopify.com/s/files/1/0808/1332/6620/files/90287317-8853-450A-A3EF-65E054DCB88B.jpg?v=1782921822",
    ],
  },
  {
    alt: "Glassing the high country",
    images: [
      "https://cdn.shopify.com/s/files/1/0808/1332/6620/files/IMG-8696.png?v=1721837343",
      "https://cdn.shopify.com/s/files/1/0808/1332/6620/files/91E8A279-FCA2-4CDC-9EDB-5AA7C6876D41.png?v=1765165220",
      "https://cdn.shopify.com/s/files/1/0808/1332/6620/files/IMG-6175.png?v=1781401861",
    ],
  },
];

function SlideshowTile({ section, index }: { section: Section; index: number }) {
  const [active, setActive] = useState(0);

  useEffect(() => {
    const prefersReduced =
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (prefersReduced || section.images.length <= 1) return;

    const startDelay = index * 800;
    let interval: ReturnType<typeof setInterval> | undefined;
    const timeout = setTimeout(() => {
      interval = setInterval(() => {
        setActive((a) => (a + 1) % section.images.length);
      }, 5000);
    }, startDelay);

    return () => {
      clearTimeout(timeout);
      if (interval) clearInterval(interval);
    };
  }, [section.images.length, index]);

  const nextIdx = (active + 1) % section.images.length;

  return (
    <div className="group relative aspect-square w-full overflow-hidden rounded-sm">
      {section.images.map((src, i) => {
        // Only mount the current slide and the next one (for smooth crossfade).
        // Everything else stays out of the DOM until it's about to be shown.
        const shouldRender = i === active || i === nextIdx;
        if (!shouldRender) return null;
        const optimized = shopifyImg(src, { width: 800, format: "webp" });
        return (
          <img
            key={`${src}-${i}`}
            src={optimized}
            srcSet={`${shopifyImg(src, { width: 500, format: "webp" })} 500w, ${optimized} 800w, ${shopifyImg(src, { width: 1200, format: "webp" })} 1200w`}
            sizes="(min-width: 768px) 33vw, 50vw"
            alt={section.alt}
            className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-700 ease-in-out group-hover:scale-105 ${
              i === active ? "opacity-100" : "opacity-0"
            }`}
            style={{ transitionProperty: "opacity, transform" }}
            loading="lazy"
            decoding="async"
            width={800}
            height={800}
          />
        );
      })}
    </div>
  );
}

export function LifestyleGallery() {
  return (
    <section className="bg-background py-20">
      <div className="mx-auto max-w-[1400px] px-6">
        <div className="text-center">
          <p className="font-ui text-xs font-bold uppercase tracking-[0.3em] text-saddle">
            The Sporting Life
          </p>
          <h2 className="mt-3 font-heading text-3xl text-primary md:text-5xl">Choose your lifestyle</h2>
        </div>

        <div className="mt-14 grid grid-cols-2 gap-6 md:grid-cols-3 md:gap-8">
          {sections.map((section, i) => (
            <SlideshowTile key={section.alt} section={section} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
